import { useState, type ReactNode } from 'react';
import { useWeapons } from '../hooks/useWeapons';

export interface UpgradeFiltros {
  /** Ids de CATEGORIAS_UPGRADE. Vazio = todas. */
  categorias: string[];
  precoMin: string;
  precoMax: string;
  floatMin: string;
  floatMax: string;
  /** Códigos de desgaste (FN, MW…). Vazio = todos. */
  exteriors: string[];
  /**
   * Uma arma específica dentro da categoria (`weapon_knife_karambit`).
   *
   * Mora aqui, e não no SkinFinder, porque depende das categorias marcadas:
   * desmarcar a categoria tem de apagar a arma junto, senão o catálogo fica
   * preso num filtro que não aparece mais em lugar nenhum.
   */
  arma: string | null;
  /**
   * Lado do agente (`ct` / `tr`). Null = os dois.
   *
   * Mora ao lado de `arma` porque é a mesma ideia: o segundo nível dentro de
   * uma categoria. Agente não tem arma, então é o lado que faz esse papel —
   * é assim que o catálogo também separa.
   */
  time: string | null;
  /** Chaves de ESTILOS_DOPPLER. Vazio = qualquer fase. */
  estilos: string[];
  /** StatTrak™: todos, só com, só sem. */
  st: 'all' | 'yes' | 'no';
  /** Só peças com adesivo aplicado. */
  comAdesivos: boolean;
  /** Mostra só o que o crédito da coluna da esquerda paga sozinho. */
  soCredito: boolean;
}

export const FILTROS_VAZIOS: UpgradeFiltros = {
  categorias: [],
  precoMin: '',
  precoMax: '',
  floatMin: '',
  floatMax: '',
  exteriors: [],
  arma: null,
  time: null,
  estilos: [],
  st: 'all',
  comAdesivos: false,
  soCredito: true,
};

/**
 * As categorias do filtro, na ordem em que quem troca skin pensa.
 *
 * Cada linha pode juntar mais de um `kind` do catálogo ("Pesadas" são
 * escopetas e metralhadoras): a API aceita um kind por chamada, então uma
 * categoria composta — ou várias marcadas — é resolvida no cliente. Ver
 * SkinFinder.
 */
export const CATEGORIAS_UPGRADE: { id: string; rotulo: string; kinds: string[] }[] = [
  { id: 'knife', rotulo: 'Facas', kinds: ['knife'] },
  { id: 'gloves', rotulo: 'Luvas', kinds: ['gloves'] },
  { id: 'rifle', rotulo: 'Rifles', kinds: ['rifle'] },
  { id: 'sniper', rotulo: 'Snipers', kinds: ['sniper'] },
  { id: 'pistol', rotulo: 'Pistolas', kinds: ['pistol'] },
  { id: 'smg', rotulo: 'SMGs', kinds: ['smg'] },
  { id: 'heavy', rotulo: 'Pesadas', kinds: ['shotgun', 'machinegun'] },
  { id: 'sticker', rotulo: 'Adesivos', kinds: ['sticker'] },
  { id: 'case', rotulo: 'Caixas', kinds: ['case'] },
  // Agentes saíram de dentro de "Outros": quem procura agente procura por
  // agente, e lá dentro ele vinha misturado com pingente, grafite e caixa.
  { id: 'agent', rotulo: 'Agentes', kinds: ['agent'] },
  { id: 'other', rotulo: 'Outros', kinds: ['other', 'charm', 'musickit', 'patch', 'graffiti'] },
];

/**
 * As fases e gemas do Doppler.
 *
 * As chaves e as cores das gemas espelham STYLES em apps/api/src/youpin/
 * styles.ts, que é a fonte da verdade — a API classifica o anúncio e devolve
 * a mesma chave. As quatro fases lá são todas cinzas (a lista da skin mostra
 * a contagem, não a cor); aqui cada uma ganha o tom que a peça tem no jogo,
 * porque é assim que quem troca Doppler as reconhece.
 *
 * Não há endpoint que sirva esta lista: /listings só devolve os estilos
 * presentes naquela skin, e esta coluna existe antes de qualquer skin aberta.
 */
export const ESTILOS_DOPPLER: { key: string; rotulo: string; cor: string }[] = [
  { key: 'p1', rotulo: 'Fase 1', cor: '#7b4fa8' },
  { key: 'p2', rotulo: 'Fase 2', cor: '#c0417a' },
  { key: 'p3', rotulo: 'Fase 3', cor: '#2aa6b8' },
  { key: 'p4', rotulo: 'Fase 4', cor: '#4a4fd0' },
  { key: 'ruby', rotulo: 'Rubi', cor: '#e0334a' },
  { key: 'sapphire', rotulo: 'Safira', cor: '#2f7de1' },
  { key: 'black_pearl', rotulo: 'Pérola Negra', cor: '#8b7fd4' },
  { key: 'emerald', rotulo: 'Esmeralda', cor: '#2eb872' },
];

/**
 * Em que família de Doppler cada fase pode aparecer.
 *
 * É o que permite recortar a GRADE de skins por fase, e não só a lista de
 * anúncios: Safira, Rubi e Pérola Negra só saem de Doppler; Esmeralda só de
 * Gamma Doppler; as quatro fases numeradas saem das duas. A peça que tem a
 * fase continua sendo decidida no anúncio, mas a skin que pode tê-la já se
 * sabe pelo nome.
 */
export type FamiliaDoppler = 'doppler' | 'gamma' | 'ambas';

const FAMILIA_DA_FASE: Record<string, FamiliaDoppler> = {
  ruby: 'doppler',
  sapphire: 'doppler',
  black_pearl: 'doppler',
  emerald: 'gamma',
  p1: 'ambas',
  p2: 'ambas',
  p3: 'ambas',
  p4: 'ambas',
};

/** O nome desta skin comporta alguma das fases marcadas? */
export function nomeAceitaFases(nome: string, estilos: string[]): boolean {
  if (estilos.length === 0) return true;
  const n = nome.toLowerCase();
  const gamma = n.includes('gamma doppler');
  const doppler = n.includes('doppler');
  return estilos.some((k) => {
    const familia = FAMILIA_DA_FASE[k];
    if (familia === 'gamma') return gamma;
    if (familia === 'doppler') return doppler && !gamma;
    return doppler;
  });
}

/** Teto do slider de preço, em reais. Acima disso vale o que for digitado. */
const TETO_PRECO = 10000;

interface Props {
  filtros: UpgradeFiltros;
  onChange: (patch: Partial<UpgradeFiltros>) => void;
  /** Ação principal da tela, no topo da coluna. Vira link no caso do login. */
  acao: { rotulo: string; onClick?: () => void; href?: string; desabilitado?: boolean };
  /** A diferença da cotação; null antes de calcular. */
  diferenca: { rotulo: string; valor: string; positivo: boolean } | null;
  credito: string | null;
  /** O resultado da troca (avisos, resumo, desconto), montado pela página. */
  resultado?: ReactNode;
}

/**
 * A coluna do meio: a ação da troca em cima, os filtros do catálogo embaixo.
 *
 * Os filtros ficam entre as duas colunas de propósito — eles pertencem ao lado
 * direito, mas quem está trocando ajusta preço e desgaste olhando o crédito da
 * esquerda. No meio, os dois números ficam no campo de visão ao mesmo tempo.
 */
export function UpgradeFilters({ filtros, onChange, acao, diferenca, credito, resultado }: Props) {
  const { data: weapons } = useWeapons();
  // Só recolhe no celular; no desktop a coluna é sempre a barra de filtros.
  const [aberto, setAberto] = useState(false);

  const alternar = (lista: string[], valor: string) =>
    lista.includes(valor) ? lista.filter((v) => v !== valor) : [...lista, valor];

  // Campo vazio vale o padrão, não zero: `Number('')` é 0 e passaria no
  // isFinite, o que colava a ponta de cima do slider na de baixo.
  const num = (texto: string, padrao: number) => {
    if (texto.trim() === '') return padrao;
    const v = Number(texto.replace(',', '.'));
    return Number.isFinite(v) ? v : padrao;
  };

  const pMin = Math.min(num(filtros.precoMin, 0), TETO_PRECO);
  const pMax = Math.min(num(filtros.precoMax, TETO_PRECO), TETO_PRECO);
  const fMin = Math.min(Math.max(num(filtros.floatMin, 0), 0), 1);
  const fMax = Math.min(Math.max(num(filtros.floatMax, 1), 0), 1);

  const temFiltro =
    filtros.categorias.length > 0 ||
    filtros.exteriors.length > 0 ||
    Boolean(filtros.arma) ||
    Boolean(filtros.time) ||
    filtros.estilos.length > 0 ||
    filtros.st !== 'all' ||
    filtros.comAdesivos ||
    Boolean(filtros.precoMin || filtros.precoMax || filtros.floatMin || filtros.floatMax);

  return (
    <div className="panel up-meio">
      <div className="up-acao">
        {acao.href ? (
          <a className="btn primary up-cta" href={acao.href}>
            {acao.rotulo}
          </a>
        ) : (
          <button className="btn primary up-cta" onClick={acao.onClick} disabled={acao.desabilitado}>
            {acao.rotulo}
          </button>
        )}

        <div className="up-diferenca">
          <span>{diferenca ? diferenca.rotulo : 'Diferença'}</span>
          <b className={diferenca?.positivo ? 'sobra' : ''}>{diferenca ? diferenca.valor : '—'}</b>
        </div>

        {resultado}
      </div>

      {/* No celular a coluna do meio vira uma barra: os filtros só abrem quando
          pedidos, senão empurrariam o catálogo para fora da primeira tela. */}
      <button className="up-meio-toggle" onClick={() => setAberto((v) => !v)}>
        <span>Filtros{temFiltro ? ' · ativos' : ''}</span>
        <i>{aberto ? '▲' : '▼'}</i>
      </button>

      <div className={`up-filtros ${aberto ? 'aberto' : ''}`}>
        <div className="up-sep" />

        <section className="up-grupo">
          <h3>Categoria</h3>
          {CATEGORIAS_UPGRADE.map((c) => (
            <label key={c.id} className="check inline up-filtro-check">
              <input
                type="checkbox"
                checked={filtros.categorias.includes(c.id)}
                onChange={() =>
                  onChange({ categorias: alternar(filtros.categorias, c.id), arma: null, time: null })
                }
              />
              <span>{c.rotulo}</span>
            </label>
          ))}
        </section>

        <section className="up-grupo">
          <h3>Preço</h3>
          {/* A faixa é em reais porque é a moeda da troca: o crédito do
              inventário e a diferença a pagar são calculados em BRL. */}
          <div className="up-faixa">
            <input
              className="input"
              inputMode="decimal"
              placeholder="mín"
              value={filtros.precoMin}
              onChange={(e) => onChange({ precoMin: e.target.value })}
            />
            <span className="up-travessao">—</span>
            <input
              className="input"
              inputMode="decimal"
              placeholder="máx"
              value={filtros.precoMax}
              onChange={(e) => onChange({ precoMax: e.target.value })}
            />
          </div>
          <FaixaDupla
            min={0}
            max={TETO_PRECO}
            step={50}
            de={pMin}
            ate={pMax}
            onDe={(v) => onChange({ precoMin: v === 0 ? '' : String(v) })}
            onAte={(v) => onChange({ precoMax: v >= TETO_PRECO ? '' : String(v) })}
          />
          {credito && (
            <label className="check inline up-filtro-check">
              <input
                type="checkbox"
                checked={filtros.soCredito}
                onChange={(e) => onChange({ soCredito: e.target.checked })}
              />
              <span>Só o que meu crédito paga ({credito})</span>
            </label>
          )}
        </section>

        <section className="up-grupo">
          <h3>Float</h3>
          <div className="up-faixa">
            <input
              className="input"
              inputMode="decimal"
              placeholder="0.00"
              value={filtros.floatMin}
              onChange={(e) => onChange({ floatMin: e.target.value })}
            />
            <span className="up-travessao">—</span>
            <input
              className="input"
              inputMode="decimal"
              placeholder="1.00"
              value={filtros.floatMax}
              onChange={(e) => onChange({ floatMax: e.target.value })}
            />
          </div>
          <FaixaDupla
            min={0}
            max={1}
            step={0.01}
            de={fMin}
            ate={fMax}
            onDe={(v) => onChange({ floatMin: v <= 0 ? '' : v.toFixed(2) })}
            onAte={(v) => onChange({ floatMax: v >= 1 ? '' : v.toFixed(2) })}
          />
        </section>

        <section className="up-grupo">
          <h3>Exterior</h3>
          {(weapons?.exteriors ?? []).map((e) => (
            <label key={e.code} className="check inline up-filtro-check">
              <input
                type="checkbox"
                checked={filtros.exteriors.includes(e.code)}
                onChange={() => onChange({ exteriors: alternar(filtros.exteriors, e.code) })}
              />
              <span>{e.pt}</span>
            </label>
          ))}
        </section>

        {/* Vale nos dois níveis, por motivos diferentes: a GRADE encolhe pelo
            nome da skin (só Doppler tem Safira, só Gamma Doppler tem
            Esmeralda), e a lista de ANÚNCIOS pela fase que a API leu de cada
            peça — duas Doppler idênticas na grade podem ser uma Rubi e uma
            Fase 2, e isso só o anúncio diz. */}
        <section className="up-grupo">
          <h3>Fase Doppler</h3>
          {ESTILOS_DOPPLER.map((e) => (
            <label key={e.key} className="check inline up-filtro-check">
              <input
                type="checkbox"
                checked={filtros.estilos.includes(e.key)}
                onChange={() => onChange({ estilos: alternar(filtros.estilos, e.key) })}
              />
              <span className="up-estilo">
                <i style={{ background: e.cor }} aria-hidden />
                {e.rotulo}
              </span>
            </label>
          ))}
          <p className="up-nota">Mostra só as skins que têm a fase; a peça exata sai ao abrir os desgastes.</p>
        </section>

        <section className="up-grupo">
          <h3>Outros</h3>
          {/* Com e sem StatTrak™ são o mesmo filtro visto dos dois lados:
              marcar um desmarca o outro, marcar os dois seria "todos". */}
          <label className="check inline up-filtro-check">
            <input
              type="checkbox"
              checked={filtros.st === 'yes'}
              onChange={(e) => onChange({ st: e.target.checked ? 'yes' : 'all' })}
            />
            <span>StatTrak™</span>
          </label>
          <label className="check inline up-filtro-check">
            <input
              type="checkbox"
              checked={filtros.st === 'no'}
              onChange={(e) => onChange({ st: e.target.checked ? 'no' : 'all' })}
            />
            <span>Sem StatTrak</span>
          </label>
          <label className="check inline up-filtro-check">
            <input
              type="checkbox"
              checked={filtros.comAdesivos}
              onChange={(e) => onChange({ comAdesivos: e.target.checked })}
            />
            <span>Com adesivos</span>
          </label>
          <p className="up-nota">Adesivo é da peça: vale ao abrir os anúncios.</p>
        </section>

        {temFiltro && (
          <button
            className="btn ghost small up-limpar"
            onClick={() =>
              onChange({
                categorias: [],
                exteriors: [],
                arma: null,
                time: null,
                estilos: [],
                st: 'all',
                comAdesivos: false,
                precoMin: '',
                precoMax: '',
                floatMin: '',
                floatMax: '',
              })
            }
          >
            Limpar filtros
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Faixa de duas pontas feita com dois <input type="range"> sobrepostos.
 *
 * Cada ponta continua sendo um controle nativo — teclado e leitor de tela
 * funcionam sem nada a mais. O trilho aceso entre as duas é pintado por baixo,
 * com as posições em porcentagem.
 */
function FaixaDupla({
  min,
  max,
  step,
  de,
  ate,
  onDe,
  onAte,
}: {
  min: number;
  max: number;
  step: number;
  de: number;
  ate: number;
  onDe: (v: number) => void;
  onAte: (v: number) => void;
}) {
  const pct = (v: number) => ((v - min) / (max - min)) * 100;
  return (
    <div className="up-slider">
      <div className="up-slider-trilho">
        <i style={{ left: `${pct(de)}%`, right: `${100 - pct(ate)}%` }} />
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={de}
        aria-label="mínimo"
        // Uma ponta não passa da outra: inverter a faixa esvaziaria a lista
        // sem que o cliente entendesse o porquê.
        onChange={(e) => onDe(Math.min(Number(e.target.value), ate))}
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={ate}
        aria-label="máximo"
        onChange={(e) => onAte(Math.max(Number(e.target.value), de))}
      />
    </div>
  );
}
